<template>
  <a-card
    title="Cycle Time Analysis (Avg, Takt Time & Variability)"
    size="small"
    class="chart-card"
    :loading="loading"
  >
    <highcharts :options="chartOptions" />
  </a-card>
</template>

<script>
import { Chart } from 'highcharts-vue';
import Highcharts from 'highcharts';
import HighchartsMore from 'highcharts/highcharts-more';
import {
  convertTime,
  convertTimeArray,
  getUnitLabel,
} from 'src/utils/timeConversion';

// Initialize highcharts-more for error bars
if (typeof HighchartsMore === 'function') {
  HighchartsMore(Highcharts);
}

export default {
  name: 'CombinedCycleAnalysisChart',
  components: {
    highcharts: Chart,
  },
  props: {
    taktData: {
      type: Object,
      default: () => ({}),
    },
    stdDeviationData: {
      type: Object,
      default: () => ({}),
    },
    loading: {
      type: Boolean,
      default: false,
    },
    timeUnit: {
      type: String,
      default: 'seconds',
      validator: (value) => ['seconds', 'minutes'].includes(value),
    },
  },
  computed: {
    chartData() {
      return Object.keys(this.taktData).length > 0
        ? this.taktData
        : {
            labels: [],
            avg_cycle_times: [],
            takt_times: [],
            percentage_diff: [],
          };
    },
    stdData() {
      return Object.keys(this.stdDeviationData).length > 0
        ? this.stdDeviationData
        : {
            labels: [],
            std_deviation_cycle_time: [],
          };
    },
    categories() {
      return this.chartData.labels || [];
    },
    unitLabel() {
      return getUnitLabel(this.timeUnit);
    },
    unitLabelAbbr() {
      return getUnitLabel(this.timeUnit, true);
    },
    taktTimeSeries() {
      return convertTimeArray(this.chartData.takt_times || [], this.timeUnit);
    },
    avgCycleTimeSeries() {
      const avgTimes = convertTimeArray(
        this.chartData.avg_cycle_times || [],
        this.timeUnit
      );
      const colors = [
        '#5470c6',
        '#91cc75',
        '#fac858',
        '#ee6666',
        '#73c0de',
        '#3ba272',
        '#fc8452',
        '#9a60b4',
      ];

      return avgTimes.map((avgTime, index) => ({
        y: avgTime,
        color: colors[index % colors.length],
      }));
    },
    percentageDiff() {
      return this.chartData.percentage_diff || [];
    },
    // Calculate error bar data based on standard deviation
    // Lower end = avg - std_dev (approximating lower quartile range)
    // Upper end = avg + std_dev (approximating upper quartile range)
    errorBarSeries() {
      const avgTimes = convertTimeArray(
        this.chartData.avg_cycle_times || [],
        this.timeUnit
      );
      const stdDevs = convertTimeArray(
        this.stdData.std_deviation_cycle_time || [],
        this.timeUnit
      );

      return avgTimes.map((avg, index) => {
        const stdDev = stdDevs[index] || 0;
        // Using ~0.675 * stdDev for quartile approximation (Q1 and Q3 in normal distribution)
        // But since we want to show the full variability range, we'll use the full std dev
        const lowerQuartile = Math.max(0, avg - stdDev);
        const upperQuartile = avg + stdDev;
        return [lowerQuartile, upperQuartile];
      });
    },
    chartOptions() {
      const unitLabel = this.unitLabel;
      const unitLabelAbbr = this.unitLabelAbbr;
      const stdDevs = convertTimeArray(
        this.stdData.std_deviation_cycle_time || [],
        this.timeUnit
      );

      return {
        chart: {
          type: 'column',
          height: 350,
          backgroundColor: 'transparent',
          zoomType: 'xy',
        },
        customData: {
          percentageDiff: this.percentageDiff,
          unitLabel: unitLabel,
          unitLabelAbbr: unitLabelAbbr,
          stdDevs: stdDevs,
        },
        title: {
          text: null,
        },
        xAxis: {
          categories: this.categories,
        },
        yAxis: {
          title: {
            text: `Time (${unitLabel})`,
          },
          labels: {
            format: `{value}${unitLabelAbbr}`,
          },
          min: 0,
        },
        legend: {
          align: 'center',
          verticalAlign: 'bottom',
          layout: 'horizontal',
        },
        plotOptions: {
          column: {
            borderRadius: 3,
            grouping: false,
            dataLabels: {
              enabled: true,
              useHTML: true,
              formatter: function () {
                const index = this.point.index;
                const series = this.series.chart.series;
                const avgCycleTimeSeries = series.find(
                  (s) => s.name === 'Average Cycle Time'
                );
                if (
                  avgCycleTimeSeries &&
                  avgCycleTimeSeries.userOptions.percentageDiff
                ) {
                  const percentDiff =
                    avgCycleTimeSeries.userOptions.percentageDiff[index];
                  if (percentDiff !== undefined) {
                    const diffColor = percentDiff > 0 ? '#ee6666' : '#91cc75';
                    const diffSign = percentDiff > 0 ? '+' : '';
                    return `<span style="color:${diffColor}; font-weight: bold; font-size: 11px;">${diffSign}${percentDiff.toFixed(
                      1
                    )}%</span>`;
                  }
                }
                return '';
              },
              y: -10,
            },
          },
          line: {
            marker: {
              enabled: true,
              radius: 5,
              symbol: 'circle',
              lineWidth: 2,
              lineColor: '#fac858',
              fillColor: 'white',
            },
            dataLabels: {
              enabled: false,
            },
          },
          errorbar: {
            color: '#F7A35C',
            lineWidth: 2,
            whiskerLength: '60%',
            whiskerWidth: 2,
            stemWidth: 2,
          },
        },
        tooltip: {
          shared: true,
          crosshairs: true,
          useHTML: true,
          backgroundColor: '#ffffff',
          borderColor: '#cccccc',
          borderRadius: 8,
          borderWidth: 1,
          shadow: true,
          style: {
            opacity: 1,
          },
          formatter: function () {
            const self = this;
            const index = self.points[0].point.index;
            const customData = self.series.chart.options.customData;
            const percentDiff = customData.percentageDiff[index];
            const unitLabelAbbr = customData.unitLabelAbbr;
            const stdDev = customData.stdDevs[index];

            // Find specific points
            const columnPoint = self.points.find(
              (p) => p.series.type === 'column'
            );
            const linePoint = self.points.find((p) => p.series.type === 'line');
            const errorBarPoint = self.points.find(
              (p) => p.series.type === 'errorbar'
            );

            let tooltip = `<div style="padding: 4px; background: #fff;">`;
            tooltip += `<div style="padding-bottom: 6px; font-weight: bold; font-size: 13px;">${self.x}</div>`;
            tooltip +=
              '<div style="border-top: 1px solid #e0e0e0; margin: 4px 0;"></div>';

            if (columnPoint) {
              tooltip += `<div style="margin: 6px 0;">`;
              tooltip += `<span style="color:${
                columnPoint.color
              }">\u25CF</span> <b>Avg Cycle Time:</b> ${columnPoint.y.toFixed(
                2
              )}${unitLabelAbbr}`;
              tooltip += `</div>`;
            }

            if (linePoint) {
              tooltip += `<div style="margin: 6px 0;">`;
              tooltip += `<span style="color:${
                linePoint.color
              }">\u25CF</span> <b>Takt Time:</b> ${linePoint.y.toFixed(
                2
              )}${unitLabelAbbr}`;
              tooltip += `</div>`;
            }

            if (stdDev !== undefined) {
              tooltip += `<div style="margin: 6px 0;">`;
              tooltip += `<span style="color:#d48265">\u25CF</span> <b>Std Deviation:</b> ±${stdDev.toFixed(
                2
              )}${unitLabelAbbr}`;
              tooltip += `</div>`;
            }

            if (errorBarPoint) {
              tooltip += `<div style="margin: 6px 0;">`;
              tooltip += `<span style="color:#666">\u25CF</span> <b>Range:</b> ${errorBarPoint.point.low.toFixed(
                2
              )}${unitLabelAbbr} – ${errorBarPoint.point.high.toFixed(
                2
              )}${unitLabelAbbr}`;
              tooltip += `</div>`;
            }

            if (percentDiff !== undefined) {
              tooltip +=
                '<div style="border-top: 1px solid #e0e0e0; margin: 6px 0;"></div>';
              const diffColor = percentDiff > 0 ? '#ee6666' : '#91cc75';
              const diffSign = percentDiff > 0 ? '+' : '';
              tooltip += `<div style="margin: 6px 0;">`;
              tooltip += `<span style="color:${diffColor}">\u25CF</span> <b>Diff from Takt:</b> ${diffSign}${percentDiff.toFixed(
                1
              )}%`;
              tooltip += `</div>`;
            }

            tooltip += `</div>`;
            return tooltip;
          },
        },
        series: [
          {
            name: 'Average Cycle Time',
            type: 'column',
            data: this.avgCycleTimeSeries,
            percentageDiff: this.percentageDiff,
            showInLegend: false,
            zIndex: 1,
          },
          {
            name: 'Variability (±Std Dev)',
            type: 'errorbar',
            data: this.errorBarSeries,
            linkedTo: ':previous',
            color: '#d48265',
            whiskerLength: '50%',
            zIndex: 3,
          },
          {
            name: 'Takt Time',
            type: 'line',
            data: this.taktTimeSeries,
            color: '#fac858',
            lineWidth: 3,
            dashStyle: 'Dash',
            marker: {
              symbol: 'circle',
              lineWidth: 2,
              lineColor: '#fac858',
              fillColor: 'white',
              radius: 6,
            },
            zIndex: 4,
          },
        ],
        credits: {
          enabled: false,
        },
      };
    },
  },
};
</script>

<style scoped>
.chart-card {
  height: 100%;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  transition: box-shadow 0.3s ease;
}

.chart-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}
</style>
